import {
	ReactElement,
	useState,
	useMemo,
	useEffect
} from "react";
import {
	Dialog,
	TableView,
	Toolbar
} from "client/Components";
import { AlertManager, useAlerts } from "client/Hooks";
import {
	CrossStoreOperationErrorModel,
	retrieveErrors
} from "client/Api/Documents/CrossStoreOperations/crossStoreActions";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentName, DocumentNameKind } from "../DocumentName";
import { OperationType } from "client/Api/Notifications/notifications";
import { never } from "client_legacy/ndaw/errors";

export type CrossStoreOperationErrorsDialogProps = {
	masterJobId: string;
	operationType: OperationType;
	onClose: () => void;
};

export const CrossStoreOperationErrorsDialog = (
	props: CrossStoreOperationErrorsDialogProps
): ReactElement | null => {
	const {
		masterJobId,
		operationType,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [crossStoreOperationErrors, setCrossStoreOperationError] =
		useState<readonly CrossStoreOperationErrorModel[]>([]);

	const alertManager: AlertManager = useAlerts();

	const dialogMessage : string = useMemo(
		(): string => {
			switch (operationType) {
				case OperationType.Move:
					return localize("_TheFollowingFoldersFilesCouldNotBeMoved");
				case OperationType.Copy:
					return localize("_TheFollowingFoldersFilesCouldNotBeCopied");
				default:
					never(operationType);
			}
		},
		[operationType]
	);

	useEffect(
		(): () => void => {
			const loadErrors = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);
				try {
					const errors: readonly CrossStoreOperationErrorModel[] = await retrieveErrors(
						masterJobId,
						signal
					);

					setCrossStoreOperationError(errors);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};
			const cancellation = new AbortController();
			loadErrors(cancellation.signal);

			return () => {
				cancellation.abort();
			};
		},
		[alertManager, masterJobId]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="cross-store-errors-dialog"
		>
			<Dialog.Header
				title={localize("_ErrorDetails")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<p>{dialogMessage}</p>
				<TableView
					trackingId="cross-store-errors-table"
					columns={[
						{
							title: localize("_Item"),
							width: "fill",
							minWidth: 176,
							trackingId: "item"
						},
						{
							title: localize("_Path"),
							minWidth: 285,
							width: "fill",
							trackingId: "path"
						},
						{
							title: localize("_Reason"),
							minWidth: 285,
							width: "fill",
							trackingId: "reason"
						}
					]}
					items={crossStoreOperationErrors}
					itemKey={x => x.path}
					hasZebraPrinting
				>
					{
						(crossStoreError, key): ReactElement => {
							return (
								<TableView.Row trackingId={key.toString()}>
									<TableView.Cell>
										<DocumentName
											documentKind={
												crossStoreError.isFolder
													? DocumentItemType.Folder
													: crossStoreError.isOneNoteNotebook
														? DocumentItemType.OneNoteNotebook
														: DocumentItemType.File
											}
											name={crossStoreError.itemName}
											mode={{
												kind: DocumentNameKind.Default,
											}}
										/>
									</TableView.Cell>
									<TableView.Cell>
										<span title={crossStoreError.path}>
											{crossStoreError.path}
										</span>
									</TableView.Cell>
									<TableView.Cell>
										<span title={crossStoreError.reason}>
											{crossStoreError.reason}
										</span>
									</TableView.Cell>
								</TableView.Row>
							);
						}
					}
				</TableView>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Close")}
						trackingId="close"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

CrossStoreOperationErrorsDialog.displayName = "CrossStoreOperationErrorsDialog";